import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api';
import { reportsService } from '../../services/reportsService';
import { formatCurrency, formatDate } from '../../utils/formatters';

const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  display: 'block',
  marginBottom: 6,
};

const thStyle: React.CSSProperties = {
  padding: '10px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  fontWeight: 500,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  borderBottom: '1px solid rgba(241,245,249,1)',
  verticalAlign: 'middle',
};

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const statValueStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 28,
  fontWeight: 300,
  background: 'linear-gradient(135deg, #d97706, #fbbf24)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  lineHeight: 1.2,
};

const filterSectionStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(92,78,51,0.15)',
  borderRadius: 6,
  padding: 20,
};

const errorStyle: React.CSSProperties = {
  background: 'rgba(201,90,90,0.08)',
  border: '1px solid rgba(201,90,90,0.2)',
  borderRadius: 4,
  padding: 16,
};

function getStatusStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 3,
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
  };
  switch (status) {
    case 'PAID':
      return { ...base, background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' };
    case 'PENDING':
      return { ...base, background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' };
    case 'DRAFT':
      return { ...base, background: 'rgba(122,106,85,0.15)', color: '#6b6a5c', border: '1px solid rgba(122,106,85,0.25)' };
    case 'OVERDUE':
      return { ...base, background: 'rgba(201,90,90,0.12)', color: '#c95a5a', border: '1px solid rgba(201,90,90,0.25)' };
    default:
      return { ...base, background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' };
  }
}

function getJobStatusStyle(status: string): React.CSSProperties {
  const base: React.CSSProperties = {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 3,
    fontFamily: "'Inter', sans-serif",
    fontSize: 9,
    fontWeight: 500,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
  };
  switch (status) {
    case 'COMPLETED':
      return { ...base, background: 'rgba(90,201,140,0.12)', color: '#5ac98c', border: '1px solid rgba(90,201,140,0.25)' };
    case 'IN_PROGRESS':
      return { ...base, background: 'rgba(90,140,201,0.12)', color: '#5a8cc9', border: '1px solid rgba(90,140,201,0.25)' };
    case 'PENDING':
      return { ...base, background: 'rgba(92,78,51,0.22)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' };
    default:
      return { ...base, background: 'rgba(122,106,85,0.15)', color: '#6b6a5c', border: '1px solid rgba(122,106,85,0.25)' };
  }
}

export const CustomerReports: React.FC = () => {
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [searchedJob, setSearchedJob] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReport, setActiveReport] = useState<'transactions' | 'jobs' | 'invoices'>('transactions');

  // Load customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers?limit=1000');
      return response.data;
    }
  });

  // Load customer transaction report
  const { data: transactionReportData, isLoading: transactionReportLoading, error: transactionReportError, refetch: refetchTransactionReport } = useQuery({
    queryKey: ['customerTransactionReport', selectedCustomer, dateRange.startDate, dateRange.endDate, selectedStatus],
    queryFn: async () => {
      console.log('🔍 Fetching customer transaction report with params:', {
        selectedCustomer,
        customerId: selectedCustomer || undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        status: selectedStatus || undefined,
        timestamp: new Date().toISOString()
      });

      const params = {
        ...(selectedCustomer && { customerId: selectedCustomer }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate }),
        ...(selectedStatus && { status: selectedStatus })
      };

      try {
        console.log('🔄 Calling reportsService with params:', params);
        const result = await reportsService.getCustomerTransactionReport(params);
        console.log('📊 Customer transaction report success:', result);
        console.log('✅ Summary data:', result?.summary);
        console.log('✅ Transactions count:', result?.transactions?.length);
        return result;
      } catch (error) {
        console.error('❌ Customer transaction report error:', error);
        throw error;
      }
    },
    enabled: true,
    retry: 1,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0
  });

  // Load job report when searched
  const { data: jobReportData, isLoading: jobReportLoading, error: jobReportError } = useQuery({
    queryKey: ['jobReport', searchedJob],
    queryFn: async () => {
      console.log('🔍 Searching for job:', searchedJob);
      const response = await api.get(`/jobs?search=${searchedJob}`);
      console.log('📊 Job search API response:', response);
      console.log('📊 Job data received:', response.data);
      const jobData = response.data.data?.[0];
      console.log('📊 First job from results:', jobData);

      // Transform flat job data to expected structure and fetch related data
      if (jobData) {
        try {
          // Fetch bills and invoices for this job
          const [billsResponse, invoicesResponse] = await Promise.all([
            api.get(`/bills?jobId=${jobData.id}`).catch(() => ({ data: { data: [] } })),
            api.get(`/invoices?jobId=${jobData.id}`).catch(() => ({ data: { data: [] } }))
          ]);

          const bills = billsResponse.data.data || [];
          const invoices = invoicesResponse.data.data || [];

          // Calculate financial totals
          const totalBills = bills.reduce((sum: number, bill: any) => sum + (bill.total || 0), 0);
          const totalInvoices = invoices.reduce((sum: number, invoice: any) => sum + (invoice.total || 0), 0);
          const profit = totalInvoices - totalBills;

          const transformedData = {
            job: {
              jobNumber: jobData.jobNumber,
              jobName: jobData.jobName,
              description: jobData.description,
              status: jobData.status,
              startDate: jobData.startDate,
              endDate: jobData.endDate,
              estimatedValue: jobData.estimatedValue,
              actualValue: jobData.actualValue,
              customerId: jobData.customerId,
              customer: jobData.customer
            },
            financials: {
              totalBills,
              totalInvoices,
              profit
            },
            bills,
            invoices
          };
          console.log('📊 Transformed job data with financials:', transformedData);
          return transformedData;
        } catch (error) {
          console.error('📊 Error fetching bills/invoices:', error);
          // Return basic job data even if bills/invoices fail
          const transformedData = {
            job: {
              jobNumber: jobData.jobNumber,
              jobName: jobData.jobName,
              description: jobData.description,
              status: jobData.status,
              startDate: jobData.startDate,
              endDate: jobData.endDate,
              estimatedValue: jobData.estimatedValue,
              actualValue: jobData.actualValue,
              customerId: jobData.customerId,
              customer: jobData.customer
            },
            financials: {
              totalBills: 0,
              totalInvoices: 0,
              profit: 0
            },
            bills: [],
            invoices: []
          };
          console.log('📊 Transformed job data (fallback):', transformedData);
          return transformedData;
        }
      }

      return null;
    },
    enabled: !!searchedJob
  });

  const reports = [
    { id: 'transactions', label: 'Customer Transactions' },
    { id: 'jobs', label: 'Job Reports' },
    { id: 'invoices', label: 'Invoice History' }
  ];

  const handleExport = async (format: 'pdf' | 'excel') => {
    try {
      let endpoint = '';
      let filename = '';

      switch (activeReport) {
        case 'transactions':
          endpoint = '/reports/customer-transactions';
          filename = `customer-transactions.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'jobs':
          endpoint = '/reports/job-report';
          filename = `job-report.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        case 'invoices':
          endpoint = '/reports/invoice-history';
          filename = `invoice-history.${format === 'excel' ? 'xlsx' : 'pdf'}`;
          break;
        default:
          endpoint = '/reports/customer-transactions';
          filename = `customer-transactions.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      }

      const params = new URLSearchParams({
        format,
        ...(selectedCustomer && { customerId: selectedCustomer }),
        ...(selectedInvoice && activeReport === 'invoices' && { invoiceNumber: selectedInvoice }),
        ...(searchedJob && activeReport === 'jobs' && { jobNumber: searchedJob }),
        ...(dateRange.startDate && { startDate: dateRange.startDate }),
        ...(dateRange.endDate && { endDate: dateRange.endDate })
      });

      const response = await api.get(`${endpoint}?${params}`, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log(`Successfully exported ${activeReport} as ${format}`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handleJobSearch = () => {
    console.log('🔍 handleJobSearch called with selectedJob:', selectedJob);
    if (selectedJob.trim()) {
      console.log('🔍 Setting searchedJob to:', selectedJob.trim());
      setSearchedJob(selectedJob.trim());
    } else {
      console.log('🔍 selectedJob is empty, not searching');
    }
  };

  const renderTransactionReport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Filters */}
      <div style={filterSectionStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Select Customer</label>
            <select
              value={selectedCustomer}
              onChange={(e) => {
                console.log('🔄 Customer selected:', e.target.value);
                setSelectedCustomer(e.target.value);
              }}
              style={{ ...inputStyle, appearance: 'none' }}
              disabled={customersLoading}
            >
              <option value="">All Customers</option>
              {customersData?.data?.map((customer: any) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>From Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>To Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>Invoice Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => {
                console.log('🔄 Status selected:', e.target.value);
                setSelectedStatus(e.target.value);
              }}
              style={{ ...inputStyle, appearance: 'none' }}
            >
              <option value="">All Statuses</option>
              <option value="PAID">Paid</option>
              <option value="PENDING">Unpaid</option>
              <option value="OVERDUE">Overdue</option>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={() => refetchTransactionReport()}
              disabled={transactionReportLoading}
              style={{
                ...primaryBtnStyle,
                opacity: transactionReportLoading ? 0.5 : 1,
                cursor: transactionReportLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {transactionReportLoading ? 'Loading...' : 'Refresh Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div>
        <h3 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 20,
          fontWeight: 300,
          color: '#1c2033',
          margin: '0 0 16px 0',
        }}>
          Customer Transaction Summary
        </h3>

        {transactionReportLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{
                ...cardStyle,
                padding: 20,
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 2,
                  background: 'rgba(92,78,51,0.25)',
                }} />
                <div style={{ height: 12, background: 'rgba(92,78,51,0.15)', borderRadius: 2, marginBottom: 10, width: '60%' }} />
                <div style={{ height: 28, background: 'rgba(239,232,213,0.6)', borderRadius: 2, width: '80%' }} />
              </div>
            ))}
          </div>
        ) : transactionReportError ? (
          <div style={errorStyle}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a', margin: '0 0 8px 0', fontWeight: 500 }}>
              Error loading transaction data
            </p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: 'rgba(201,90,90,0.7)', margin: '0 0 12px 0' }}>
              {(transactionReportError as any)?.message || 'Unknown error'}
            </p>
            <button onClick={() => refetchTransactionReport()} style={{
              ...primaryBtnStyle,
              padding: '7px 16px',
              fontSize: 9,
            }}>
              Retry
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {/* Total Invoices */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #d97706, #fbbf24)' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Total Invoices
              </p>
              <p style={{ ...statValueStyle, fontSize: 32, margin: 0 }}>
                {transactionReportData?.summary?.totalInvoices || 0}
              </p>
            </div>

            {/* Total Revenue */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5ac98c, rgba(90,201,140,0.3))' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Total Revenue
              </p>
              <p style={{ ...statValueStyle, margin: 0 }}>
                {formatCurrency(transactionReportData?.summary?.totalRevenue || 0)}
              </p>
            </div>

            {/* Payments Received */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5a8cc9, rgba(90,140,201,0.3))' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Payments Received
              </p>
              <p style={{ ...statValueStyle, margin: 0 }}>
                {formatCurrency(transactionReportData?.summary?.paymentsReceived || 0)}
              </p>
            </div>

            {/* Outstanding */}
            <div style={{ ...cardStyle, padding: 20, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #c95a5a, rgba(201,90,90,0.3))' }} />
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                Outstanding
              </p>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 28,
                fontWeight: 300,
                color: '#c95a5a',
                margin: 0,
                lineHeight: 1.2,
              }}>
                {formatCurrency(transactionReportData?.summary?.outstanding || 0)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Transactions table */}
      <div style={cardStyle}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
          <h3 style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 20,
            fontWeight: 300,
            color: '#1c2033',
            margin: 0,
          }}>
            Recent Transactions
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(248,250,252,1)' }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Customer</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {transactionReportLoading ? (
                [1, 2, 3, 4, 5].map(i => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6].map(j => (
                      <td key={j} style={tdStyle}>
                        <div style={{ height: 12, background: 'rgba(239,232,213,0.6)', borderRadius: 2, width: j === 5 ? '70%' : '85%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : transactionReportData?.transactions && transactionReportData.transactions.length > 0 ? (
                transactionReportData.transactions.map((transaction: any, index: number) => (
                  <tr key={transaction.id || index} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,250,252,1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={tdStyle}>{formatDate(transaction.date)}</td>
                    <td style={tdStyle}>{transaction.customer}</td>
                    <td style={tdStyle}>{transaction.type}</td>
                    <td style={{ ...tdStyle, color: '#d97706', fontWeight: 500 }}>{transaction.reference}</td>
                    <td style={{ ...tdStyle, fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#d97706', fontWeight: 400 }}>
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td style={tdStyle}>
                      <span style={getStatusStyle(transaction.status)}>{transaction.status}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ ...tdStyle, textAlign: 'center', padding: '32px 16px', color: '#6b6a5c' }}>
                    {selectedCustomer
                      ? `No transactions found for the selected customer${dateRange.startDate ? ' in the specified date range' : ''}`
                      : `No transactions found${dateRange.startDate ? ' in the specified date range' : ''}`
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderJobReport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Search filter */}
      <div style={filterSectionStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Job Number</label>
            <input
              type="text"
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              placeholder="Enter job number (e.g., JOB-2024-001)"
              style={inputStyle}
            />
          </div>
          <button
            onClick={handleJobSearch}
            disabled={!selectedJob.trim() || jobReportLoading}
            style={{
              ...primaryBtnStyle,
              opacity: (!selectedJob.trim() || jobReportLoading) ? 0.5 : 1,
              cursor: (!selectedJob.trim() || jobReportLoading) ? 'not-allowed' : 'pointer',
            }}
          >
            {jobReportLoading ? 'Searching...' : 'Search Job'}
          </button>
        </div>
      </div>

      {jobReportError && (
        <div style={errorStyle}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#c95a5a', margin: 0 }}>
            Error loading job report: {(jobReportError as any).message || 'Job not found'}
          </p>
        </div>
      )}

      {!searchedJob && (
        <div style={{ ...filterSectionStyle, textAlign: 'center', padding: '32px 20px' }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
            Enter a job number and click "Search Job" to view detailed financial information
          </p>
        </div>
      )}

      {searchedJob && !jobReportError && (
        <div style={filterSectionStyle}>
          <h3 style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 20,
            fontWeight: 300,
            color: '#1c2033',
            margin: '0 0 16px 0',
          }}>
            Job Financial Summary{jobReportData?.job?.jobNumber ? ` — ${jobReportData?.job?.jobNumber}` : ''}
          </h3>

          {jobReportLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} style={{ ...cardStyle, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'rgba(92,78,51,0.25)' }} />
                  <div style={{ height: 10, background: 'rgba(92,78,51,0.15)', borderRadius: 2, marginBottom: 10, width: '60%' }} />
                  <div style={{ height: 24, background: 'rgba(239,232,213,0.6)', borderRadius: 2, width: '80%' }} />
                </div>
              ))}
            </div>
          ) : jobReportData ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
                {/* Total Bills */}
                <div style={{ ...cardStyle, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #c95a5a, rgba(201,90,90,0.3))' }} />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                    Total Bills
                  </p>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 22,
                    fontWeight: 300,
                    color: '#c95a5a',
                    margin: 0,
                    lineHeight: 1.2,
                  }}>
                    {formatCurrency(jobReportData?.financials?.totalBills || 0)}
                  </p>
                </div>

                {/* Total Invoices */}
                <div style={{ ...cardStyle, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5ac98c, rgba(90,201,140,0.3))' }} />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                    Total Invoices
                  </p>
                  <p style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 22,
                    fontWeight: 300,
                    color: '#5ac98c',
                    margin: 0,
                    lineHeight: 1.2,
                  }}>
                    {formatCurrency(jobReportData?.financials?.totalInvoices || 0)}
                  </p>
                </div>

                {/* Profit */}
                <div style={{ ...cardStyle, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #d97706, #fbbf24)' }} />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                    Profit
                  </p>
                  <p style={{ ...statValueStyle, fontSize: 22, margin: 0 }}>
                    {formatCurrency(jobReportData?.financials?.profit || 0)}
                  </p>
                </div>

                {/* Start Date */}
                <div style={{ ...cardStyle, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #5a8cc9, rgba(90,140,201,0.3))' }} />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                    Start Date
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52', margin: 0 }}>
                    {jobReportData?.job?.startDate ? new Date(jobReportData?.job?.startDate).toLocaleDateString() : 'N/A'}
                  </p>
                </div>

                {/* Status */}
                <div style={{ ...cardStyle, padding: 16, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'rgba(92,78,51,0.3)' }} />
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 10px 0' }}>
                    Status
                  </p>
                  <span style={getJobStatusStyle(jobReportData?.job?.status || '')}>
                    {jobReportData?.job?.status || 'Unknown'}
                  </span>
                </div>
              </div>

              {jobReportData?.job?.description && (
                <div style={{ ...cardStyle, padding: 16, marginTop: 16 }}>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.15em', textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                    Description
                  </p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#5b5a52', margin: 0, lineHeight: 1.6 }}>
                    {jobReportData?.job?.description}
                  </p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
                {/* Bills list */}
                <div style={cardStyle}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
                    <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 300, color: '#1c2033', margin: 0 }}>
                      Bills ({jobReportData.bills?.length || 0})
                    </h4>
                  </div>
                  <div style={{ padding: 16 }}>
                    {jobReportData.bills && jobReportData.bills.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {jobReportData.bills.slice(0, 5).map((bill: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'rgba(248,250,252,1)',
                            borderRadius: 4,
                            border: '1px solid rgba(239,232,213,0.6)',
                          }}>
                            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                              {bill.billNumber || `Bill #${index + 1}`}
                            </span>
                            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#c95a5a', fontWeight: 400 }}>
                              {formatCurrency(bill.total || 0)}
                            </span>
                          </div>
                        ))}
                        {jobReportData.bills.length > 5 && (
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: '4px 0 0 0' }}>
                            ... and {jobReportData.bills.length - 5} more
                          </p>
                        )}
                      </div>
                    ) : (
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                        No bills found for this job
                      </p>
                    )}
                  </div>
                </div>

                {/* Invoices list */}
                <div style={cardStyle}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(92,78,51,0.15)' }}>
                    <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 18, fontWeight: 300, color: '#1c2033', margin: 0 }}>
                      Invoices ({jobReportData.invoices?.length || 0})
                    </h4>
                  </div>
                  <div style={{ padding: 16 }}>
                    {jobReportData.invoices && jobReportData.invoices.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {jobReportData.invoices.slice(0, 5).map((invoice: any, index: number) => (
                          <div key={index} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'rgba(248,250,252,1)',
                            borderRadius: 4,
                            border: '1px solid rgba(239,232,213,0.6)',
                          }}>
                            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>
                              {invoice.invoiceNumber || `Invoice #${index + 1}`}
                            </span>
                            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 15, color: '#5ac98c', fontWeight: 400 }}>
                              {formatCurrency(invoice.total || 0)}
                            </span>
                          </div>
                        ))}
                        {jobReportData.invoices.length > 5 && (
                          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', margin: '4px 0 0 0' }}>
                            ... and {jobReportData.invoices.length - 5} more
                          </p>
                        )}
                      </div>
                    ) : (
                      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                        No invoices found for this job
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                No job data found for "{searchedJob}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderInvoiceReport = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Search filter */}
      <div style={filterSectionStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Invoice Number</label>
            <input
              type="text"
              value={selectedInvoice}
              onChange={(e) => setSelectedInvoice(e.target.value)}
              placeholder="Enter invoice number (e.g., INV-2024-001)"
              style={inputStyle}
            />
          </div>
          <button style={primaryBtnStyle}>
            Search Invoice
          </button>
        </div>
      </div>

      {/* Invoice lifecycle */}
      <div>
        <h3 style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 20,
          fontWeight: 300,
          color: '#1c2033',
          margin: '0 0 16px 0',
        }}>
          Invoice Lifecycle History
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Invoice Created */}
          <div style={{
            ...cardStyle,
            padding: 16,
            borderLeft: '3px solid #5a8cc9',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Invoice Created
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  January 15, 2024 at 10:30 AM
                </p>
              </div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#5a8cc9' }}>
                {formatCurrency(5670.50)}
              </span>
            </div>
          </div>

          {/* Invoice Sent */}
          <div style={{
            ...cardStyle,
            padding: 16,
            borderLeft: '3px solid #d97706',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Invoice Sent
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  January 15, 2024 at 2:15 PM
                </p>
              </div>
              <span style={getStatusStyle('PENDING')}>Pending</span>
            </div>
          </div>

          {/* Payment Received */}
          <div style={{
            ...cardStyle,
            padding: 16,
            borderLeft: '3px solid #5ac98c',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4 style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.15em', color: '#1c2033', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                  Payment Received
                </h4>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', margin: 0 }}>
                  January 28, 2024 at 9:45 AM
                </p>
              </div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, fontWeight: 300, color: '#5ac98c' }}>
                {formatCurrency(5670.50)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeReport) {
      case 'transactions':
        return renderTransactionReport();
      case 'jobs':
        return renderJobReport();
      case 'invoices':
        return renderInvoiceReport();
      default:
        return renderTransactionReport();
    }
  };

  return (
    <div style={{ padding: 24, background: '#f4efe2', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 28,
              fontWeight: 300,
              color: '#1c2033',
              margin: 0,
              letterSpacing: '0.05em',
            }}>
              Customer Reports
            </h2>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, color: '#6b6a5c', letterSpacing: '0.3em', textTransform: 'uppercase', margin: '4px 0 0 0' }}>
              Financial Intelligence
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => handleExport('excel')}
              style={ghostBtnStyle}
            >
              Export Excel
            </button>
            <button
              onClick={() => handleExport('pdf')}
              style={primaryBtnStyle}
            >
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div style={{ borderBottom: '1px solid rgba(92,78,51,0.15)', marginBottom: 24, display: 'flex', gap: 4 }}>
        {reports.map((report) => {
          const isActive = activeReport === report.id;
          return (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id as any)}
              style={{
                padding: '10px 20px',
                fontFamily: "'Inter', sans-serif",
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                border: 'none',
                borderBottom: isActive ? '2px solid #d97706' : '2px solid transparent',
                background: isActive ? 'rgba(239,232,213,0.6)' : 'transparent',
                color: isActive ? '#d97706' : '#8a8778',
                transition: 'all 0.15s',
                marginBottom: -1,
                whiteSpace: 'nowrap',
              }}
            >
              {report.label}
            </button>
          );
        })}
      </div>

      {/* Report content */}
      {renderContent()}
    </div>
  );
};
